// Split from theme.js: miscellaneous UI widgets (lines 6327-7131, 7265-7350, 7822-7961, 7994-8418)
class LogoList extends HTMLElement {
  constructor() {
    super()

    Motion.inView(this, this.init.bind(this), { margin: '200px 0px 200px 0px' })
  }

  get childElement() {
    return this.firstElementChild
  }

  get speed() {
    return this.hasAttribute('data-speed') ? parseInt(this.getAttribute('data-speed')) : 16
  }

  get maximum() {
    return this.hasAttribute('data-maximum') ? parseInt(this.getAttribute('data-maximum')) : Math.ceil(this.parentWidth / this.childElementWidth) + 2
  }

  get direction() {
    return this.hasAttribute('data-direction') ? this.getAttribute('data-direction') : 'left'
  }

  get parentWidth() {
    return this.getWidth(this)
  }

  get childElementWidth() {
    return this.getWidth(this.childElement)
  }

  disconnectedCallback() {
    if (this.marquee) {
      this.pause()
      this.marquee.destroy()
    }
  }

  init() {
    if (this.childElementCount === 1) {
      this.childElement.classList.add('animated')

      const maximum = this.maximum
      for (let index = 0; index < maximum; index++) {
        this.clone = this.childElement.cloneNode(true)
        this.clone.setAttribute('aria-hidden', true)
        this.appendChild(this.clone)
        this.clone.querySelectorAll('.media').forEach((media) => media.classList.remove('loading'))
      }

      if (theme.config.isTouch) {
        this.style.setProperty('--duration', `${33 - this.speed}s`)
      } else {
        this.marquee = new Flickity(this, {
          prevNextButtons: false,
          pageDots: false,
          wrapAround: true,
          freeScroll: true,
          rightToLeft: this.direction === 'right'
        })

        // Set initial position to be 0
        this.marquee.x = 0

        // Start the marquee animation
        this.play()

        this.addEventListener('mouseenter', this.pause)
        this.addEventListener('mouseleave', this.play)
      }
    }
  }

  play() {
    // Set the decrement of position x
    this.marquee.x -= this.speed * 0.1

    // Settle position into the slider
    this.marquee.settle(this.marquee.x)

    // Set the requestId to the local variable
    this.requestId = requestAnimationFrame(this.play.bind(this))
  }

  pause() {
    if (this.requestId) {
      // Cancel the animation
      cancelAnimationFrame(this.requestId)

      // Reset the requestId for the next animation.
      this.requestId = undefined
    }
  }

  getWidth(element) {
    const rect = element.getBoundingClientRect()
    return rect.right - rect.left
  }
}
customElements.define('logo-list', LogoList)

class TextScrolling extends HTMLElement {
  constructor() {
    super()

    this.items.forEach((item) => {
      const header = item.querySelector('.heading')

      Motion.scroll(Motion.animate(header, { opacity: [0, 0, 1, 1, 1, 0, 0] }), { target: header, offsets: ['33vh', '66vh'] })
    })
  }

  get items() {
    return (this._items = this._items || Array.from(this.children))
  }
}
customElements.define('text-scrolling', TextScrolling)

class TabsElement extends HTMLElement {
  constructor() {
    super()

    this.selectedIndex = this.selectedIndex
    this.buttons.forEach((button, index) => button.addEventListener('click', () => (this.selectedIndex = index)))

    if (Shopify.designMode) {
      this.addEventListener('shopify:block:select', (event) => (this.selectedIndex = this.buttons.indexOf(event.target)))
    }
  }

  static get observedAttributes() {
    return ['selected-index']
  }

  get selectedIndex() {
    return parseInt(this.getAttribute('selected-index')) || 0
  }

  set selectedIndex(index) {
    this.setAttribute('selected-index', Math.min(Math.max(index, 0), this.buttons.length - 1).toString())
  }

  get buttons() {
    return (this._buttons = this._buttons || Array.from(this.querySelectorAll('button[role="tab"]')))
  }

  get indicators() {
    return (this._indicators = this._indicators || Array.from(this.querySelectorAll('.indicators')))
  }

  load() {
    const toButton = this.buttons[parseInt(this.selectedIndex)]
    if (toButton === undefined) return

    const toPanel = document.getElementById(toButton.getAttribute('aria-controls'))
    Motion.animate(
      toPanel,
      { transform: ['translateY(2rem)', 'translateY(0)'], opacity: [0, 1] },
      { duration: theme.config.motionReduced ? 0 : 0.15 }
    )

    const motionList = toPanel.querySelector('motion-list')
    if (motionList && motionList.initialized) {
      motionList.load()
    }
  }

  unload() {
    const fromButton = this.buttons[parseInt(this.selectedIndex)]
    if (fromButton === undefined) return

    const fromPanel = document.getElementById(fromButton.getAttribute('aria-controls'))
    Motion.animate(
      fromPanel,
      { transform: ['translateY(0)', 'translateY(2rem)'], opacity: [1, 0] },
      { duration: theme.config.motionReduced ? 0 : 0.15 }
    )

    const motionList = fromPanel.querySelector('motion-list')
    if (motionList && motionList.initialized) {
      motionList.unload()
    }
  }

  attributeChangedCallback(name, oldValue, newValue) {
    this.buttons.forEach((button, index) => {
      button.classList.toggle('button--primary', index === parseInt(newValue))
      button.classList.toggle('button--secondary', index !== parseInt(newValue))
      button.disabled = index === parseInt(newValue)
    })

    this.indicators.forEach((indicators, index) => {
      indicators.hidden = index !== parseInt(newValue)
      if (index === parseInt(newValue)) {
        indicators.querySelectorAll('button').forEach((button) => {
          button.unload()
          button.load()
        })
      }
    })

    if (name === 'selected-index' && oldValue !== null && oldValue !== newValue) {
      const fromButton = this.buttons[parseInt(oldValue)]
      const toButton = this.buttons[parseInt(newValue)]
      this.transition(
        document.getElementById(fromButton.getAttribute('aria-controls')),
        document.getElementById(toButton.getAttribute('aria-controls'))
      )
    }
  }

  async transition(fromPanel, toPanel) {
    if (fromPanel) {
      await Motion.animate(
        fromPanel,
        { transform: ['translateY(0)', 'translateY(2rem)'], opacity: [1, 0] },
        { duration: theme.config.motionReduced ? 0 : 0.15 }
      ).finished
    }

    fromPanel.hidden = true
    toPanel.hidden = false
    Motion.animate(
      toPanel,
      { transform: ['translateY(2rem)', 'translateY(0)'], opacity: [0, 1] },
      { duration: theme.config.motionReduced ? 0 : 0.15 }
    )

    const motionList = toPanel.querySelector('motion-list')
    if (motionList && motionList.initialized) {
      motionList.load()
    }

    theme.a11y.trapFocus(this, toPanel.querySelector('slider-element') || toPanel.querySelector('a'))
  }
}
customElements.define('tabs-element', TabsElement)

class CountdownTimer extends HTMLElement {
  constructor() {
    super()

    Motion.inView(this, this.init.bind(this), { margin: '200px 0px 200px 0px' })
  }

  get date() {
    return (this._date = this._date || new Date(this.getAttribute('data-expires')))
  }

  get isCompact() {
    return this.getAttribute('data-compact') === 'true' || (this.getAttribute('data-compact') === 'mobile' && theme.config.mqlSmall)
  }

  init() {
    this.calculate()
    this.timerInterval = setInterval(this.calculate.bind(this), 1000)
  }

  calculate() {
    const now = new Date(),
      countTo = new Date(this.date),
      timeDifference = countTo - now

    if (timeDifference < 0) {
      this.complete()
      return
    }

    const secondsInADay = 60 * 60 * 1000 * 24,
      secondsInAHour = 60 * 60 * 1000

    const days = Math.floor((timeDifference / secondsInADay) * 1)
    const hours = Math.floor(((timeDifference % secondsInADay) / secondsInAHour) * 1)
    const mins = Math.floor((((timeDifference % secondsInADay) % secondsInAHour) / (60 * 1000)) * 1)
    const secs = Math.floor(((((timeDifference % secondsInADay) % secondsInAHour) % (60 * 1000)) / 1000) * 1)

    if (this.isCompact) {
      const dayHTML = days > 0 ? `<div class="countdown__item"><p>${days}${theme.dateStrings.d}</p></div>` : ''
      const hourHTML = `<div class="countdown__item"><p>${hours}${theme.dateStrings.h}</p></div>`
      const minHTML = `<div class="countdown__item"><p>${mins}${theme.dateStrings.m}</p></div>`
      const secHTML = `<div class="countdown__item"><p>${secs}${theme.dateStrings.s}</p></div>`

      this.innerHTML = dayHTML + hourHTML + minHTML + secHTML
    } else {
      const dayHTML =
        days > 0 ? `<div class="countdown__item"><p>${days}</p><span>${days === 1 ? theme.dateStrings.day : theme.dateStrings.days}</span></div>` : ''
      const hourHTML = `<div class="countdown__item"><p>${hours}</p><span>${hours === 1 ? theme.dateStrings.hour : theme.dateStrings.hours}</span></div>`
      const minHTML = `<div class="countdown__item"><p>${mins}</p><span>${mins === 1 ? theme.dateStrings.minute : theme.dateStrings.minutes}</span></div>`
      const secHTML = `<div class="countdown__item"><p>${secs}</p><span>${secs === 1 ? theme.dateStrings.second : theme.dateStrings.seconds}</span></div>`

      this.innerHTML = dayHTML + hourHTML + minHTML + secHTML
    }
  }

  complete() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval)
    }

    this.hidden = true
  }
}
customElements.define('countdown-timer', CountdownTimer)

class ImageComparison extends HTMLElement {
  constructor() {
    super()

    Motion.inView(this, async () => {
      await theme.utils.imageLoaded(this.media)
      this.init()
    })
  }

  get button() {
    return this.querySelector('button')
  }

  get bounding() {
    return this.getBoundingClientRect()
  }

  get horizontal() {
    return this.getAttribute('data-layout') === 'horizontal'
  }

  get media() {
    return Array.from(this.querySelectorAll('img, svg'))
  }

  init() {
    this.active = false

    this.button.addEventListener('touchstart', this.startHandler.bind(this), theme.supportsPassive ? { passive: false } : false)
    document.body.addEventListener('touchend', this.endHandler.bind(this), theme.supportsPassive ? { passive: true } : false)
    document.body.addEventListener('touchmove', this.onHandler.bind(this), theme.supportsPassive ? { passive: true } : false)

    this.button.addEventListener('mousedown', this.startHandler.bind(this))
    document.body.addEventListener('mouseup', this.endHandler.bind(this))
    document.body.addEventListener('mousemove', this.onHandler.bind(this))

    setTimeout(() => this.animate(), 1e3)
  }

  animate() {
    this.setAttribute('animate', '')

    this.classList.add('animated')
    setTimeout(() => {
      this.classList.remove('animated')
    }, 1e3)
  }

  startHandler(event) {
    event.preventDefault()
    this.active = true
    this.classList.add('scrolling')
  }

  endHandler() {
    this.active = false
    this.classList.remove('scrolling')
  }

  onHandler(e) {
    if (!this.active) return

    const event = (e.touches && e.touches[0]) || e
    let x = this.horizontal ? event.pageX - (this.bounding.left + window.scrollX) : event.pageY - (this.bounding.top + window.scrollY)

    this.scrollIt(x)
  }

  scrollIt(x) {
    const distance = this.horizontal ? this.clientWidth : this.clientHeight

    const max = distance - 20
    const min = 20
    const mouseX = Math.max(min, Math.min(x, max))
    const mousePercent = (mouseX * 100) / distance
    this.style.setProperty('--percent', mousePercent + '%')
  }
}
customElements.define('image-comparison', ImageComparison)

class LookbookElement extends HTMLElement {
  constructor() {
    super()

    Motion.inView(this, async () => {
      await theme.utils.imageLoaded(this.media)
      this.init()
    })
  }

  get media() {
    return Array.from(this.querySelectorAll('img, svg'))
  }

  get items() {
    return (this._items = this._items || Array.from(this.querySelectorAll('.hotspot')))
  }

  init() {
    this.items.forEach((item) => item.addEventListener('mouseenter', (event) => this.select(this.items.indexOf(event.target))))

    if (Shopify.designMode) {
      const section = this.closest('.shopify-section')
      section.addEventListener('shopify:section:select', this.animate.bind(this))
      section.addEventListener('shopify:section:deselect', this.closeAll.bind(this))
      this.addEventListener('shopify:block:select', (event) => this.open(this.items.indexOf(event.target)))
    }

    setTimeout(() => this.animate(), 1e3)
  }

  animate() {
    this.openAll()

    this.timer = setTimeout(() => this.closeAll(), 3e3)
  }

  open(selectedIndex) {
    this.items.forEach((item, index) => item.classList.toggle('active', selectedIndex === index))
  }

  openAll() {
    this.items.forEach((item) => item.classList.add('active'))
  }

  closeAll() {
    this.items.forEach((item) => item.classList.remove('active'))
    clearTimeout(this.timer)
  }

  select(selectedIndex) {
    this.items.forEach((item, index) => item.setAttribute('aria-current', selectedIndex === index ? 'true' : 'false'))
    this.dispatchEvent(new CustomEvent('lookbook:change', { bubbles: true, detail: { index: selectedIndex } }))
  }
}
customElements.define('lookbook-element', LookbookElement)

class ShopTheLook extends HTMLElement {
  constructor() {
    super()

    this.lookbook.addEventListener('lookbook:change', (event) => this.carousel.select(event.detail.index))
    this.carousel.addEventListener('carousel:change', (event) => this.lookbook.select(event.detail.index))
  }

  get lookbook() {
    return this.querySelector('lookbook-element')
  }

  get carousel() {
    return this.querySelector('carousel-element')
  }
}
customElements.define('shop-the-look', ShopTheLook)

class SpinningText extends HTMLElement {
  constructor() {
    super()

    if (!theme.config.isTouch || Shopify.designMode) {
      Motion.inView(this, this.init.bind(this), { margin: '600px 0px 600px 0px' })
    } else {
      new theme.initWhenVisible(this.init.bind(this))
    }
  }

  get string() {
    let string = this.getAttribute('data-string')
    string = string.replace(/  +/g, ' ')
    return '•' + string.replace(/ +/g, '•')
  }

  init() {
    if (this.initialized) return
    this.initialized = true

    const canTrig = CSS.supports('(top: calc(sin(1) * 1px))')
    const OPTIONS = {
      TEXT: this.string,
      SIZE: 2,
      SPACING: 2
    }

    const HEADING = document.createElement('div')
    const text = OPTIONS.TEXT

    // Take the text and split it into spans...
    const chars = text.split('')
    this.style.setProperty('--char-count', chars.length)

    for (let c = 0; c < chars.length; c++) {
      HEADING.innerHTML += `<span aria-hidden="true" class="split-char" style="--char-index: ${c};">${chars[c]}</span>`
    }
    HEADING.innerHTML += `<span class="sr-only">${OPTIONS.TEXT}</span>`
    HEADING.classList = 'split-chars'

    // Set the styles
    this.style.setProperty('--font-size', OPTIONS.SIZE)
    this.style.setProperty('--character-width', OPTIONS.SPACING)
    this.style.setProperty(
      '--radius',
      canTrig
        ? 'calc((var(--character-width) / sin(var(--inner-angle))) * -1ch)'
        : `calc(
        (${OPTIONS.SPACING} / ${Math.sin(360 / this.children.length / (180 / Math.PI))})
        * -1ch
      )`
    )

    // Append
    this.appendChild(HEADING)
  }
}
customElements.define('spinning-text', SpinningText)

class SlideshowElement extends HTMLElement {
  constructor() {
    super()

    this.selectedIndex = this.selectedIndex

    if (!theme.config.isTouch || Shopify.designMode) {
      Motion.inView(this, this.init.bind(this), { margin: '200px 0px 200px 0px' })
    } else {
      new theme.initWhenVisible(theme.utils.throttle(this.init.bind(this)))
    }
  }

  static get observedAttributes() {
    return ['selected-index']
  }

  get selectedIndex() {
    return parseInt(this.getAttribute('selected-index')) || 0
  }

  set selectedIndex(index) {
    this.setAttribute('selected-index', Math.min(Math.max(index, 0), this.items.length - 1).toString())
  }

  get items() {
    return (this._items = this._items || Array.from(this.children))
  }

  get autoplay() {
    return this.hasAttribute('autoplay')
  }

  get speed() {
    return this.hasAttribute('autoplay') ? parseInt(this.getAttribute('autoplay-speed')) * 1000 : 5000
  }

  get adaptiveHeightProperty() {
    return false
  }

  init() {
    if (this.initialized) return
    this.initialized = true

    const that = this
    if (this.items.length > 1) {
      this.slider = new Flickity(this, {
        accessibility: false,
        pageDots: false,
        prevNextButtons: false,
        wrapAround: true,
        rightToLeft: theme.config.rtl,
        autoPlay: this.autoplay ? this.speed : false,
        adaptiveHeightProperty: this.adaptiveHeightProperty,
        on: {
          ready: function () {
            const { selectedElement } = this
            that.onReady(selectedElement)
          }
        }
      })

      this.slider.on('change', this.onChange.bind(this))
      this.addEventListener('slider:previous', () => this.slider.previous())
      this.addEventListener('slider:next', () => this.slider.next())
      this.addEventListener('slider:play', () => this.slider.playPlayer())
      this.addEventListener('slider:pause', () => this.slider.pausePlayer())

      if (Shopify.designMode) {
        this.addEventListener('shopify:block:select', (event) => this.slider.select(this.items.indexOf(event.target)))
      }
    } else if (this.items.length === 1) {
      const selectedElement = this.firstChild
      that.onReady(selectedElement)
    }
  }

  disconnectedCallback() {
    if (this.slider) this.slider.destroy()
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'selected-index' && oldValue !== null && oldValue !== newValue) {
      const fromElement = this.items[parseInt(oldValue)]
      const toElement = this.items[parseInt(newValue)]

      const animateElement = toElement.querySelector('animate-element')
      animateElement?.refresh()

      if (fromElement.getAttribute('data-type') === 'video') {
        const fromVideoElement = theme.utils.visibleMedia(fromElement.querySelectorAll('video-media'))
        fromVideoElement?.pause()
      }

      if (toElement.getAttribute('data-type') === 'video') {
        const toVideoElement = theme.utils.visibleMedia(toElement.querySelectorAll('video-media'))
        toVideoElement?.play()
      }
    }
  }

  onChange() {
    this.selectedIndex = this.slider.selectedIndex
    this.dispatchEvent(new CustomEvent('slider:change', { bubbles: true, detail: { currentPage: this.slider.selectedIndex } }))
  }

  select(selectedIndex) {
    this.slider.select(selectedIndex)
  }

  onReady(selectedElement) {
    if (selectedElement.getAttribute('data-type') === 'video') {
      const videoElement = theme.utils.visibleMedia(selectedElement.querySelectorAll('video-media'))
      videoElement?.play()
    }

    if (!theme.config.isTouch && document.body.hasAttribute('data-page-transition')) {
      const animateElement = selectedElement.querySelector('animate-element')
      animateElement?.refresh()
    }
  }
}
customElements.define('slideshow-element', SlideshowElement)

class SlideshowHero extends SlideshowElement {
  constructor() {
    super()

    window.addEventListener('scroll', theme.utils.throttle(this.onScrollHandler.bind(this)), false)
  }

  get adaptiveHeightProperty() {
    return true
  }

  get heroSection() {
    return this.closest('.hero-section')
  }

  get nextSection() {
    let nextSibling = this.heroSection.nextElementSibling
    for (let i = 0; i < 20; i++) {
      if (nextSibling.classList.contains('shopify-section') && !nextSibling.classList.contains('header-section')) break
      nextSibling = nextSibling.nextElementSibling
    }

    if (nextSibling && nextSibling.nodeType != 1) {
      nextSibling = document.getElementById('PageContainer')
    }

    return nextSibling
  }

  onScrollHandler() {
    const bounding = this.nextSection.getBoundingClientRect()
    this.heroSection.classList.toggle('hero-scrolled', bounding.y < -30)
  }
}
customElements.define('slideshow-hero', SlideshowHero)

class SlideshowWords extends HTMLElement {
  constructor() {
    super()

    this.selectedIndex = this.selectedIndex

    if (this.controlledElement) {
      this.controlledElement.addEventListener('slider:change', (event) => (this.selectedIndex = event.detail.currentPage))
    }
  }

  static get observedAttributes() {
    return ['selected-index']
  }

  get selectedIndex() {
    return parseInt(this.getAttribute('selected-index')) || 0
  }

  set selectedIndex(index) {
    this.setAttribute('selected-index', Math.min(Math.max(index, 0), this.items.length - 1).toString())
  }

  get controlledElement() {
    return this.hasAttribute('aria-controls') ? document.getElementById(this.getAttribute('aria-controls')) : null
  }

  get items() {
    return (this._items = this._items || Array.from(this.children))
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'selected-index' && oldValue !== null && oldValue !== newValue) {
      this.transition(this.items[parseInt(oldValue)], this.items[parseInt(newValue)])
    }
  }

  transition(fromElement, toElement) {
    const fromWords = Array.from(fromElement.querySelectorAll('animate-element'))
    const toWords = Array.from(toElement.querySelectorAll('animate-element'))

    fromWords.forEach((element) => element.reset())

    setTimeout(
      () => {
        this.items.forEach((item) => {
          item.setAttribute('aria-current', parseInt(item.getAttribute('data-index')) === parseInt(this.selectedIndex) ? 'true' : 'false')
        })

        toWords.forEach((element) => element.refresh())
      },
      500 + 30 * fromWords.length
    )
  }
}
customElements.define('slideshow-words', SlideshowWords, { extends: 'nav' })

class SlideshowParallax extends HTMLDivElement {
  constructor() {
    super()

    this.load()
  }

  get controlledElement() {
    return this.hasAttribute('aria-controls') ? document.getElementById(this.getAttribute('aria-controls')) : null
  }

  get mobileDisabled() {
    return false
  }

  load() {
    if (theme.config.motionReduced) return
    if (this.mobileDisabled && (theme.config.isTouch || theme.config.mqlSmall)) return

    this.motion()
  }

  motion() {
    this.animation = Motion.scroll(
      Motion.animate(this, { transform: [`translateY(${this.getAttribute('data-start')})`, `translateY(${this.getAttribute('data-stop')})`] }),
      { target: this.controlledElement ?? document.body, offset: Motion.ScrollOffset.Exit }
    )
  }
}
customElements.define('slideshow-parallax', SlideshowParallax, { extends: 'div' })

class ControlButton extends HTMLButtonElement {
  constructor() {
    super()

    this.addEventListener('click', this.onClick)
  }

  get controlledElement() {
    return this.hasAttribute('aria-controls') ? document.getElementById(this.getAttribute('aria-controls')) : null
  }

  get paused() {
    return this.hasAttribute('paused')
  }

  static get observedAttributes() {
    return ['paused']
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'paused') {
      if (oldValue === null && newValue === '') {
        return (this.controlledElement ?? this).dispatchEvent(new CustomEvent('slider:pause', { bubbles: true }))
      }

      if (newValue === null) {
        return (this.controlledElement ?? this).dispatchEvent(new CustomEvent('slider:play', { bubbles: true }))
      }
    }
  }

  onClick() {
    this.paused ? this.removeAttribute('paused') : this.setAttribute('paused', '')
  }
}
customElements.define('control-button', ControlButton, { extends: 'button' })
class RevealBanner extends HTMLElement {
  constructor() {
    super()

    Motion.inView(this, this.init.bind(this), { margin: '600px 0px 600px 0px' })
  }

  init() {
    const tracker = this.querySelector('.reveal-banner__tracker')
    Motion.scroll(
      Motion.animate(
        this.querySelectorAll('.banner__media'),
        { clipPath: ['inset(20% 35% 0% 35% round var(--rounded-block))', 'inset(0% 0%)'] },
        { easing: 'linear' }
      ),
      { target: tracker, offset: Motion.ScrollOffset.Enter }
    )

    const tracker2nd = this.querySelector('.reveal-banner__tracker2nd')
    if (tracker2nd) {
      Motion.scroll(
        Motion.timeline([
          [this.querySelector('.banner__overlay'), { opacity: [0, 1] }, { easing: 'linear' }],
          [
            this.querySelector('.banner__content'),
            { opacity: [0, 1], visibility: ['hidden', 'visible'], transform: ['translateY(10%)', 'translateY(0)'] },
            { easing: 'linear', at: '<' }
          ]
        ]),
        { target: tracker2nd, offset: Motion.ScrollOffset.Enter }
      )
    }
  }
}
customElements.define('reveal-banner', RevealBanner)

class TestimonialParallax extends ParallaxElement {
  constructor() {
    super()
  }

  get mobileDisabled() {
    return true
  }
}
customElements.define('testimonial-parallax', TestimonialParallax, { extends: 'div' })

class SplittingBanner extends HTMLElement {
  constructor() {
    super()

    Motion.inView(this, this.init.bind(this), { margin: '600px 0px 600px 0px' })
  }

  init() {
    const splitting = Splitting({ target: this.querySelector('.splitting'), by: 'chars', whitespace: true })

    const wrapper = this.querySelector('.splitting-wrapper')
    const tracker = this.querySelector('.reveal-banner__tracker')
    Motion.scroll(
      Motion.timeline([
        [splitting[0].chars, { opacity: [0.3, 1] }, { delay: theme.config.motionReduced ? 0 : Motion.stagger(0.1) }]
        //[wrapper, { transform: ['translateY(-20vh)', 'translateY(0)'] }, { easing: 'linear', at: '<' }]
      ]),
      { target: tracker, offset: Motion.ScrollOffset.Enter }
    )

    Motion.scroll(Motion.animate(wrapper, { opacity: theme.config.mqlSmall ? [1, 0, 0] : [1, 1, 0] }, { easing: 'linear' }), {
      target: tracker,
      offset: Motion.ScrollOffset.Exit
    })

    const tracker2nd = this.querySelector('.reveal-banner__tracker2nd')
    if (tracker2nd) {
      Motion.scroll(
        Motion.animate(
          this.querySelector('.button-wrapper'),
          { opacity: [0, 1, 1], transform: ['translateY(100%)', 'translateY(0)'] },
          { easing: 'linear' }
        ),
        { target: tracker2nd, offset: Motion.ScrollOffset.Enter }
      )
    }
  }
}
customElements.define('splitting-banner', SplittingBanner)
class NumberCounter extends HTMLElement {
  constructor() {
    super()

    if (theme.config.motionReduced) return

    if (!theme.config.isTouch || Shopify.designMode) {
      Motion.inView(this, this.init.bind(this), { margin: '200px 0px 200px 0px' })
    } else {
      new theme.initWhenVisible(this.init.bind(this))
    }
  }

  init() {
    if (this.initialized) return
    this.initialized = true

    const matches = this.textContent.trim().match(/\d+(?:[,. ]\d+)*/)
    const toReplace = matches[0].replace(/[,\. ]+/, '')

    Motion.animate(
      (progress) => {
        const formattedString = Math.round(progress * parseInt(toReplace)).toString()
        this.textContent = progress === 1 ? matches[0] : formattedString
      },
      { duration: parseFloat(this.getAttribute('data-duration')), easing: [0.16, 1, 0.3, 1] }
    )
  }
}
customElements.define('number-counter', NumberCounter)

class ScrollingBanner extends HTMLElement {
  constructor() {
    super()

    if (!theme.config.isTouch || Shopify.designMode) {
      Motion.inView(this, this.init.bind(this), { margin: '600px 0px 600px 0px' })
    } else {
      new theme.initWhenVisible(this.init.bind(this))
    }
  }

  get media() {
    return (this._media = this._media || Array.from(this.querySelectorAll('.image-with-text__image>.media')))
  }

  get stickyElement() {
    return this.querySelector('sticky-element')
  }

  init() {
    if (this.initialized) return
    this.initialized = true

    this.detectScrollListener = theme.utils.throttle(this.onScrollHandler.bind(this))

    const mql = window.matchMedia('screen and (min-width: 1024px)')
    if (mql.matches) this.load()
    mql.onchange = (mql) => {
      mql.matches ? this.load() : this.unload()
    }
  }

  setMediaHeight() {
    let height = 0
    this.media.forEach((media) => {
      height += media.getBoundingClientRect().height
    })
    this.style.setProperty('--scrolling-height', `${height.toFixed(1)}px`)
  }

  onScrollHandler() {
    const scrollTop = window.scrollY

    if (this.currentScrollTop === scrollTop) return
    this.currentScrollTop = scrollTop
    const offsetTop = this.getBoundingClientRect().top - parseFloat(this.stickyElement.inset)

    const mediaElements = this.media
    const mediaCount = mediaElements.length

    if (offsetTop < 0) {
      let activeIndex = 1

      // Start from second element
      for (let i = 1; i < mediaCount; i++) {
        const media = mediaElements[i]
        const mediaHeight = media.getBoundingClientRect().height
        const mediaScrollTop = mediaHeight * (i - 1)

        if (Math.abs(offsetTop) > mediaScrollTop) {
          activeIndex = i
          let progress = 100 - Math.floor(((Math.abs(offsetTop) - mediaScrollTop) / mediaHeight) * 100)
          if (progress > 95) progress = 100
          if (progress < 5) progress = 0

          media.style.clipPath = `polygon(0 ${progress}%, 100% ${progress}%, 100% 100%, 0 100%)`
          this.updateControlledElement(media, progress)
        } else {
          media.style.clipPath = ''
        }
      }

      // Update remaining elements
      for (let i = activeIndex + 1; i < mediaCount; i++) {
        const media = mediaElements[i]
        this.updateControlledElement(media, 100)
      }
    } else {
      // Reset all elements when scrolled to top
      for (let i = 1; i < mediaCount; i++) {
        const media = mediaElements[i]
        media.style.clipPath = ''
        this.updateControlledElement(media, 100)
      }
    }
  }

  updateControlledElement(media, progress) {
    const controlledElement = media.hasAttribute('aria-controls') ? document.getElementById(media.getAttribute('aria-controls')) : null
    if (controlledElement) {
      if (progress === 100) controlledElement.classList.add('opacity-0', 'pointer-events-none')
      if (progress === 0) controlledElement.classList.remove('opacity-0', 'pointer-events-none')
    }
  }

  load() {
    this.setMediaHeight()
    window.addEventListener('scroll', this.detectScrollListener, false)
  }

  unload() {
    window.removeEventListener('scroll', this.detectScrollListener)
    this.style.removeProperty('--scrolling-height')
    this.media.forEach((media) => {
      media.style.clipPath = ''
    })
  }
}
customElements.define('scrolling-banner', ScrollingBanner)

class SocialFeed extends XModal {
  constructor() {
    super()

    this.onKeyup = this.onKeyup.bind(this)
  }

  get shouldCloseAll() {
    return true
  }

  get lookbook() {
    return this.querySelector('lookbook-element')
  }

  get previousButton() {
    return this.querySelector('button[data-button="previous"]')
  }

  get nextButton() {
    return this.querySelector('button[data-button="next"]')
  }

  connectedCallback() {
    super.connectedCallback()
    this.addEventListener('keyup', this.onKeyup)
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.removeEventListener('keyup', this.onKeyup)
  }

  beforeShow() {
    super.beforeShow()
    this.lookbook.closeAll()
  }

  async afterShow() {
    super.afterShow()
    Motion.inView(this.lookbook, async () => {
      await theme.utils.imageLoaded(this.lookbook.media)
      this.lookbook.animate()
    })
  }

  onKeyup(event) {
    if (event.code === 'ArrowLeft' || event.code === 'ArrowRight') {
      const button = event.code === 'ArrowLeft' ? this.previousButton : this.nextButton
      if (button && !button.disabled) {
        event.preventDefault()
        button.dispatchEvent(new Event('click'))
      }
    }
  }
}
customElements.define('social-feed', SocialFeed)

class SocialFeedButton extends HTMLButtonElement {
  constructor() {
    super()

    this.addEventListener('click', this.onButtonClick)
  }

  get controlledElement() {
    return this.hasAttribute('aria-controls') ? document.getElementById(this.getAttribute('aria-controls')) : null
  }

  onButtonClick() {
    if (this.controlledElement) this.controlledElement.show()
  }
}
customElements.define('social-feed-button', SocialFeedButton, { extends: 'button' })

class IconsCarousel extends HTMLDivElement {
  constructor() {
    super()

    this.onUpdateTabindex()

    this.onUpdateTabindex = this.onUpdateTabindex.bind(this)
    this.onEnterListener = this.onEnterHandler.bind(this)
    this.onLeaveListener = this.onLeaveHandler.bind(this)
  }

  get isScrollable() {
    const style = window.getComputedStyle(this)
    const hasScrollableOverflow =
      style.overflow === 'scroll' ||
      style.overflow === 'auto' ||
      style.overflowY === 'scroll' ||
      style.overflowY === 'auto' ||
      style.overflowX === 'scroll' ||
      style.overflowX === 'auto'
    const hasScrollableContent = this.scrollHeight > this.clientHeight || this.scrollWidth > this.clientWidth

    return hasScrollableOverflow && hasScrollableContent
  }

  connectedCallback() {
    if (theme.config.isTouch) return

    this.addEventListener('mouseenter', this.onEnterListener)
    this.addEventListener('mouseleave', this.onLeaveListener)
    document.addEventListener('matchSmall', this.onUpdateTabindex)
    document.addEventListener('unmatchSmall', this.onUpdateTabindex)
  }

  disconnectedCallback() {
    if (theme.config.isTouch) return

    this.removeEventListener('mouseenter', this.onEnterListener)
    this.removeEventListener('mouseleave', this.onLeaveListener)
    document.removeEventListener('matchSmall', this.onUpdateTabindex)
    document.removeEventListener('unmatchSmall', this.onUpdateTabindex)
  }

  onEnterHandler() {
    const scrollWidth = this.scrollWidth * (theme.config.rtl ? -1 : 1)
    this.scrollTo({
      left: scrollWidth,
      behavior: theme.config.motionReduced ? 'auto' : 'smooth'
    })
  }

  onLeaveHandler() {
    this.scrollTo({
      left: 0,
      behavior: theme.config.motionReduced ? 'auto' : 'smooth'
    })
  }

  onUpdateTabindex() {
    this.removeAttribute('tabindex')

    if (this.isScrollable) {
      this.setAttribute('tabindex', '0')
    }
  }
}
customElements.define('icons-carousel', IconsCarousel, { extends: 'div' })

class ComparisonTable extends HTMLElement {
  constructor() {
    super()

    this.highlighted = false
    this.tableSticky = this.querySelector('.comparison-table__sticky')
    if (this.tableSticky) {
      Motion.inView(this, this.init.bind(this))
    }
  }

  get items() {
    return Array.from(this.querySelectorAll('.comparison-table__row'))
  }

  init() {
    this.addEventListener('scroll', theme.utils.throttle(this.onScrollHandler.bind(this)))
    this.tableSticky.addEventListener('scroll', theme.utils.throttle(this.onStickyScrollHandler.bind(this)))

    Motion.scroll(
      ({ y }) => {
        if (y.current >= y.targetOffset + this.tableSticky.clientHeight / 2 && y.progress < 0.85) {
          this.tableSticky.classList.add('active')
        } else {
          this.tableSticky.classList.remove('active')
        }
      },
      { target: this, offset: ['0 start', 'end 0'] }
    )
  }

  onScrollHandler(event) {
    this.tableSticky.scrollTo({
      left: event.target.scrollLeft
    })
  }

  onStickyScrollHandler(event) {
    this.scrollTo({
      left: event.target.scrollLeft
    })
  }

  highlight() {
    this.items.forEach((item) => {
      const classesToCheck = ['thead', 'tfoot', 'sticky']
      if (classesToCheck.every((className) => !item.classList.contains(className))) {
        if (this.highlighted) {
          const columns = Array.from(item.querySelectorAll('.comparison-table__column'))
          const first_column = columns[0]

          let highlighted = false
          columns.forEach((column) => {
            if (first_column.innerHTML != column.innerHTML) {
              highlighted = true
            }
          })

          item.classList.toggle('info', highlighted)
        } else {
          item.classList.remove('info')
        }
      }
    })
  }
}
customElements.define('comparison-table', ComparisonTable)

class ProductComparison extends HTMLSelectElement {
  constructor() {
    super()

    this.oldValue = this.value
    this.addEventListener('change', this.onChange)
  }

  get sectionId() {
    return this.getAttribute('data-section-id')
  }

  get column() {
    return this.getAttribute('data-column')
  }

  get controlledElement() {
    return this.hasAttribute('aria-controls') ? document.getElementById(this.getAttribute('aria-controls')) : null
  }

  get items() {
    return Array.from(this.controlledElement.querySelectorAll('select[is="product-comparison"]'))
  }

  get selectedArr() {
    return this.items.map((item) => (item !== this ? item.value : this.oldValue))
  }

  onChange() {
    const index = this.selectedArr.indexOf(this.value)
    if (index !== -1) {
      this.changeSelected(this.value)

      const sibling = this.items[index]
      if (sibling) {
        sibling.value = this.oldValue
        sibling.dispatchEvent(new Event('change'))
      }
    } else {
      this.changeSelected(this.value)
    }
    this.oldValue = this.value
  }

  changeSelected(productId) {
    const blockJSON = theme.ProductComparisonJSON[this.sectionId]?.[productId]
    if (typeof blockJSON === 'undefined') return

    const column = this.items.indexOf(this) + 1

    for (let index = 1; index <= 25; index++) {
      const element = this.controlledElement.querySelector(`[data-column="${column}"][data-index="${index}"]`)
      if (element) {
        const heading = blockJSON[`heading_${index}`]
        const value = blockJSON[`value_${index}`]
        element.innerHTML = heading.length ? `<p class="h6 leading-tight tracking-none">${heading}</p>${this.unescape(value)}` : this.unescape(value)
      }
    }

    const product = this.controlledElement.querySelector(`[data-product="${column}"]`)
    if (product) {
      product.innerHTML = document.getElementById(`ProductComparisonInfo-${this.sectionId}-${productId}`).innerHTML
    }

    const productSticky = this.controlledElement.querySelector(`[data-product-sticky="${column}"]`)
    if (productSticky) {
      productSticky.innerHTML = document.getElementById(`ProductComparisonInfo-${this.sectionId}-${productId}`).innerHTML
    }

    const productAction = this.controlledElement.querySelector(`[data-product-action="${column}"]`)
    if (productAction) {
      productAction.innerHTML = document.getElementById(`ProductComparisonAction-${this.sectionId}-${productId}`).innerHTML
    }

    if (this.controlledElement) {
      this.controlledElement.highlight()
    }
  }

  unescape(input) {
    const doc = new DOMParser().parseFromString(input, 'text/html')
    return doc.documentElement.textContent
  }
}
customElements.define('product-comparison', ProductComparison, { extends: 'select' })

class ComparisonHighlight extends HTMLInputElement {
  constructor() {
    super()

    this.addEventListener('change', this.onChange)
  }

  get controlledElement() {
    return this.hasAttribute('aria-controls') ? document.getElementById(this.getAttribute('aria-controls')) : null
  }

  get items() {
    return Array.from(this.controlledElement.querySelectorAll('.comparison-table__row'))
  }

  onChange() {
    if (this.controlledElement) {
      this.controlledElement.highlighted = this.checked
      this.controlledElement.highlight()
    }
  }
}
customElements.define('comparison-highlight', ComparisonHighlight, { extends: 'input' })

class ScrollSpy extends HTMLElement {
  constructor() {
    super()
  }

  get scrollspyLinks() {
    return Array.from(this.querySelectorAll('.scrollspy-link'))
  }

  get backToTopButton() {
    return this.querySelector('.back-to-top')
  }

  get headerHeight() {
    let header = document.querySelector('.header-section header[is="sticky-header"]')
    if (this.hasAttribute('aria-controls')) {
      header = document.getElementById(this.getAttribute('aria-controls'))
    }
    return header ? header.getBoundingClientRect().height + this.topGap : this.offsetHeight < window.innerHeight ? this.topGap : 0
  }

  get topGap() {
    return this.hasAttribute('data-gap') ? parseInt(this.getAttribute('data-gap')) : 20
  }

  get showHighlight() {
    return this.hasAttribute('data-highlight')
  }

  connectedCallback() {
    this.scrollspyLinks.forEach((link) => {
      link.addEventListener('click', this.handleScrollspyLinkClick.bind(this))
    })

    if (this.backToTopButton) {
      this.backToTopButton.addEventListener('click', this.handleBackToTopClick.bind(this))
    }

    if (this.showHighlight) {
      window.addEventListener('scroll', this.updateActiveLink.bind(this))
      this.updateActiveLink.bind(this)
    }
  }

  handleScrollspyLinkClick(event) {
    event.preventDefault()
    const link = event.currentTarget

    const targetIds = link.hasAttribute('data-target') ? link.getAttribute('data-target').split(',') : []
    const section = document.getElementById(targetIds[0])

    if (section) {
      const sectionTop = window.scrollY + section.getBoundingClientRect().top - this.headerHeight
      window.scrollTo({
        top: sectionTop,
        behavior: theme.config.motionReduced ? 'auto' : 'smooth'
      })
    }
  }

  handleBackToTopClick() {
    window.scrollTo({
      top: 0,
      behavior: theme.config.motionReduced ? 'auto' : 'smooth'
    })
  }

  updateActiveLink() {
    let current = ''

    this.scrollspyLinks.forEach((link) => {
      const targetIds = link.hasAttribute('data-target') ? link.getAttribute('data-target').split(',') : []

      targetIds.forEach((targetId) => {
        const section = document.getElementById(targetId)

        if (section) {
          const offset = 0
          const sectionTop = section.offsetTop - offset
          const sectionBottom = sectionTop + section.offsetHeight

          if (window.pageYOffset >= sectionTop && window.pageYOffset < sectionBottom) {
            current = targetId
          }
        }
      })
    })

    this.scrollspyLinks.forEach((link) => {
      const targetIds = link.hasAttribute('data-target') ? link.getAttribute('data-target').split(',') : []
      let active = false

      targetIds.forEach((targetId) => {
        if (targetId !== '' && targetId === current) {
          active = true
        }
      })

      link.classList.toggle('active', active)
    })
  }
}
customElements.define('scroll-spy', ScrollSpy, { extends: 'nav' })
